import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Chip,
  Alert,
  Stack,
  Divider,
  LinearProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import decisionSupportService from "../API/decisionSupportService";
import { useAuth } from "../Context/AuthContext";
import TokenExpired from "../Components/TokenExpired";

import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningIcon from "@mui/icons-material/Warning";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentIcon from "@mui/icons-material/Assignment";
import InsightsIcon from "@mui/icons-material/Insights";
import ShieldIcon from "@mui/icons-material/Shield";

const FONT_FAMILY = '"Montserrat", sans-serif';

const formatCurrency = (value) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "MAD",
    maximumFractionDigits: 0,
  }).format(value ?? 0);

const formatNumber = (value) =>
  Number(value ?? 0).toLocaleString("fr-FR");

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color,
  bg,
  action,
  onClick,
}) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      p: 2.5,
      borderRadius: "22px",
      border: "1px solid #e2e8f0",
      background: "#ffffff",
      boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
      transition: "0.25s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.12)",
      },
    }}
  >
    <Stack direction="row" alignItems="center" spacing={2}>
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: "16px",
          bgcolor: bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color, fontSize: 28 }} />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            color: "#64748b",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            color: "#0f172a",
            fontSize: 26,
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>

    <Typography
      sx={{
        mt: 2,
        minHeight: 40,
        color: "#64748b",
        fontFamily: FONT_FAMILY,
        fontSize: 13,
      }}
    >
      {subtitle}
    </Typography>

    {action && (
      <Button
        fullWidth
        variant="contained"
        onClick={onClick}
        sx={{
          mt: 2,
          borderRadius: "14px",
          textTransform: "none",
          fontFamily: FONT_FAMILY,
          fontWeight: 400,
          bgcolor: color,
          boxShadow: "none",
          "&:hover": {
            bgcolor: color,
            opacity: 0.9,
            boxShadow: "none",
          },
        }}
      >
        {action}
      </Button>
    )}
  </Card>
);

const PriorityCard = ({ action, navigate }) => {
  const Icon = action.icon;

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        p: 2.5,
        borderRadius: "22px",
        border: "1px solid #e2e8f0",
        borderLeft: `5px solid ${action.color}`,
        background: "#ffffff",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
        transition: "0.25s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 18px 45px rgba(15, 23, 42, 0.12)",
        },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: "15px",
            bgcolor: action.bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ color: action.color, fontSize: 24 }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: 16,
              fontWeight: 600,
              color: "#0f172a",
            }}
          >
            {action.title}
          </Typography>

          <Chip
            size="small"
            label={action.priority === "high" ? "Urgent" : "Important"}
            sx={{
              mt: 0.5,
              height: 23,
              fontFamily: FONT_FAMILY,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: `${action.color}18`,
              color: action.color,
            }}
          />
        </Box>
      </Stack>

      <Typography
        sx={{
          color: "#64748b",
          fontFamily: FONT_FAMILY,
          fontSize: 13,
          minHeight: 42,
          mb: 2,
        }}
      >
        {action.description}
      </Typography>

      <Stack spacing={1.2} sx={{ mb: 2 }}>
        {(action.details || []).map((detail, index) => (
          <Box
            key={index}
            sx={{
              p: 1.5,
              borderRadius: "14px",
              bgcolor: "#f8fafc",
              border: "1px solid #eef2f7",
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontWeight: 400,
                color: "#1e293b",
                fontSize: 13,
              }}
            >
              {detail.vehicle_immatriculation ||
                detail.route ||
                detail.document_type ||
                "Détail"}
            </Typography>

            {detail.utilization_percentage && (
              <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                Utilisation : {detail.utilization_percentage}%
              </Typography>
            )}

            {detail.profitability_percentage && (
              <Typography sx={{ color: "#10b981", fontSize: 12 }}>
                Rentabilité : +{detail.profitability_percentage}%
              </Typography>
            )}

            {detail.count && (
              <Typography sx={{ color: "#ef4444", fontSize: 12 }}>
                {detail.count} documents
              </Typography>
            )}

            {detail.kilometrage && (
              <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                {formatNumber(detail.kilometrage)} km
              </Typography>
            )}
          </Box>
        ))}
      </Stack>

      <Button
        fullWidth
        variant="outlined"
        onClick={() => navigate(action.path)}
        sx={{
          borderRadius: "14px",
          textTransform: "none",
          fontFamily: FONT_FAMILY,
          fontWeight: 600,
          color: action.color,
          borderColor: `${action.color}55`,
          "&:hover": {
            borderColor: action.color,
            bgcolor: `${action.color}10`,
          },
        }}
      >
        Voir détails
      </Button>
    </Card>
  );
};

const QuickStats = ({ data, navigate }) => {
  if (!data) return null;

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} sm={6} lg={2.4}>
        <StatCard
          title="Véhicules actifs"
          icon={DirectionsCarIcon}
          color="#0ea5e9"
          bg="rgba(14,165,233,0.12)"
          value={data.resourceOptimization?.details?.vehicleStatus?.active || 0}
          subtitle={`Sur ${data.resourceOptimization?.details?.vehicleStatus?.total || 0
            } véhicules au total`}
          action="Voir véhicules"
          onClick={() => navigate("/Transport/Vehicules")}
        />
      </Grid>

      <Grid item xs={12} sm={6} lg={2.4}>
        <StatCard
          title="Revenus ce mois"
          icon={TrendingUpIcon}
          color="#22c55e"
          bg="rgba(34,197,94,0.12)"
          value={formatCurrency(
            data.revenueOpportunity?.details?.revenueMetrics
              ?.current_month_revenue || 0
          )}
          subtitle={`${data.revenueOpportunity?.details?.revenueMetrics
              ?.growth_percentage || 0
            }% par rapport au mois dernier`}
          action="Voir voyages"
          onClick={() => navigate("/Transport/Voyages")}
        />
      </Grid>

      <Grid item xs={12} sm={6} lg={2.4}>
        <StatCard
          title="Maintenance urgente"
          icon={WarningIcon}
          color="#f59e0b"
          bg="rgba(245,158,11,0.14)"
          value={
            data.maintenancePlanning?.details?.vehiclesNeedingMaintenance
              ?.length || 0
          }
          subtitle="Véhicules nécessitent une maintenance"
          action="Planifier"
          onClick={() => navigate("/Transport/Vehicules")}
        />
      </Grid>

      <Grid item xs={12} sm={6} lg={2.4}>
        <StatCard
          title="Documents expirés"
          icon={DescriptionIcon}
          color="#ef4444"
          bg="rgba(239,68,68,0.12)"
          value={data.documentAlerts?.expired_count || 0}
          subtitle={`${data.documentAlerts?.expiring_soon_count || 0
            } documents expirent bientôt`}
          action="Vérifier"
          onClick={() => navigate("/Documents/Alerts")}
        />
      </Grid>

      <Grid item xs={12} sm={6} lg={2.4}>
        <StatCard
          title="Efficacité flotte"
          icon={CheckCircleIcon}
          color="#8b5cf6"
          bg="rgba(139,92,246,0.12)"
          value={`${data.resourceOptimization?.details?.currentEfficiency
              ?.efficiency_score || 0
            }%`}
          subtitle={`${data.resourceOptimization?.details?.currentEfficiency
              ?.voyages_per_vehicle || 0
            } voyages par véhicule`}
          action="Analyser"
          onClick={() => navigate("/Transport/Voyages")}
        />
      </Grid>
    </Grid>
  );
};

const buildActions = (data) => {
  const actions = [];

  if (
    data.documentAlerts?.expired_count > 0 ||
    data.documentAlerts?.expiring_soon_count > 0
  ) {
    actions.push({
      id: "documents",
      title: "Documents à vérifier",
      description: `${data.documentAlerts.expired_count || 0} expirés, ${data.documentAlerts.expiring_soon_count || 0
        } expirent bientôt.`,
      priority: "high",
      icon: DescriptionIcon,
      color: "#ef4444",
      bgColor: "rgba(239,68,68,0.12)",
      path: "/Documents/Alerts",
      details: data.documentAlerts.expiring_by_type?.slice(0, 3) || [],
    });
  }

  if (
    data.maintenancePlanning?.details?.vehiclesNeedingMaintenance?.length > 0
  ) {
    actions.push({
      id: "maintenance",
      title: "Maintenance préventive",
      description: `${data.maintenancePlanning.details.vehiclesNeedingMaintenance.length
        } véhicules nécessitent une intervention.`,
      priority: "high",
      icon: WarningIcon,
      color: "#f59e0b",
      bgColor: "rgba(245,158,11,0.14)",
      path: "/Transport/Vehicules",
      details:
        data.maintenancePlanning.details.vehiclesNeedingMaintenance.slice(
          0,
          3
        ),
    });
  }

  if (data.revenueOpportunity?.details?.mostProfitableRoutes?.length > 0) {
    const topRoute =
      data.revenueOpportunity.details.mostProfitableRoutes[0];

    actions.push({
      id: "revenue",
      title: "Opportunité de revenus",
      description: `Itinéraire ${topRoute.route} très rentable (+${topRoute.profitability_percentage}%).`,
      priority: "medium",
      icon: TrendingUpIcon,
      color: "#10b981",
      bgColor: "rgba(16,185,129,0.12)",
      path: "/Transport/Voyages",
      details:
        data.revenueOpportunity.details.mostProfitableRoutes.slice(0, 2),
    });
  }

  if (
    data.resourceOptimization?.details?.underutilizedVehicles?.length > 0
  ) {
    actions.push({
      id: "optimization",
      title: "Optimisation flotte",
      description: `${data.resourceOptimization.details.underutilizedVehicles.length
        } véhicules sous-utilisés détectés.`,
      priority: "medium",
      icon: DirectionsCarIcon,
      color: "#6366f1",
      bgColor: "rgba(99,102,241,0.12)",
      path: "/Transport/Vehicules",
      details:
        data.resourceOptimization.details.underutilizedVehicles.slice(0, 3),
    });
  }

  return actions;
};

const PriorityActions = ({ data, navigate }) => {
  if (!data) return null;

  const actions = buildActions(data);

  return (
    <Card
      elevation={0}
      sx={{
        mt: 3,
        p: 3,
        borderRadius: "26px",
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: 22,
              fontWeight: 600,
              color: "#0f172a",
            }}
          >
            Actions prioritaires
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              color: "#64748b",
              fontSize: 14,
            }}
          >
            Les points les plus importants à traiter pour améliorer la performance.
          </Typography>
        </Box>

        <Chip
          icon={<AssignmentIcon />}
          label={`${actions.length} action(s)`}
          sx={{
            borderRadius: "12px",
            bgcolor: "#eef2ff",
            color: "#4f46e5",
            fontFamily: FONT_FAMILY,
            fontWeight: 600,
          }}
        />
      </Stack>

      {actions.length === 0 ? (
        <Alert
          severity="success"
          sx={{
            borderRadius: "18px",
            fontFamily: FONT_FAMILY,
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>
            Excellente gestion !
          </Typography>
          <Typography>
            Aucune action urgente requise. Votre flotte est bien suivie.
          </Typography>
        </Alert>
      ) : (
        <Grid container spacing={2.5}>
          {actions.map((action) => (
            <Grid item xs={12} md={6} lg={3} key={action.id}>
              <PriorityCard action={action} navigate={navigate} />
            </Grid>
          ))}
        </Grid>
      )}
    </Card>
  );
};

export default function DecisionSupport() {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [role, setRole] = useState(null);
  const { TokenExp } = useAuth();

  const fetchRecommendations = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await decisionSupportService.getRecommendations();
      setRecommendations(data);
    } catch (e) {
      setError("Impossible de charger les données. Vérifiez votre connexion.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const stored = sessionStorage.getItem("authLocalstorage");
    const parsed = stored ? JSON.parse(stored) : null;

    setToken(parsed?.token ?? false);
    setRole(parsed?.user?.role?.name ?? null);

    fetchRecommendations();
  }, [TokenExp]);

  if (token === false) {
    return <TokenExpired />;
  }

  if (!["user-facture", "admin", "super-admin"].includes(role)) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 2, md: 4 },
        background:
          "linear-gradient(180deg, #f8fafc 0%, #eef2ff 45%, #f8fafc 100%)",
      }}
    >
      <Card
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 4 },
          borderRadius: "30px",
          overflow: "hidden",
          position: "relative",
          background:
            "linear-gradient(135deg, #0f172a 0%, #1e3a8a 55%, #4f46e5 100%)",
          color: "#ffffff",
          boxShadow: "0 24px 60px rgba(30, 58, 138, 0.25)",
          "&:before": {
            content: '""',
            position: "absolute",
            width: 260,
            height: 260,
            borderRadius: "50%",
            right: -80,
            top: -100,
            background: "rgba(255,255,255,0.12)",
          },
          "&:after": {
            content: '""',
            position: "absolute",
            width: 160,
            height: 160,
            borderRadius: "50%",
            right: 120,
            bottom: -90,
            background: "rgba(255,255,255,0.08)",
          },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Box>
            <Chip
              icon={<InsightsIcon sx={{ color: "#ffffff !important" }} />}
              label="Centre d’aide à la décision"
              sx={{
                mb: 2,
                bgcolor: "rgba(255,255,255,0.16)",
                color: "#ffffff",
                fontFamily: FONT_FAMILY,
                fontWeight: 600,
                borderRadius: "12px",
              }}
            />

            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: { xs: 30, md: 42 },
                fontWeight: 600,
                letterSpacing: "-0.04em",
                lineHeight: 1.1,
              }}
            >
              Pilotage intelligent de la flotte
            </Typography>

            <Typography
              sx={{
                mt: 1.5,
                maxWidth: 680,
                color: "rgba(255,255,255,0.78)",
                fontFamily: FONT_FAMILY,
                fontSize: 15,
              }}
            >
              Suivez les revenus, documents, maintenances et opportunités
              d’optimisation depuis une seule interface.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchRecommendations}
            disabled={loading}
            sx={{
              borderRadius: "16px",
              px: 3,
              py: 1.3,
              bgcolor: "#ffffff",
              color: "#1e3a8a",
              textTransform: "none",
              fontFamily: FONT_FAMILY,
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#f8fafc",
                boxShadow: "none",
              },
            }}
          >
            Actualiser
          </Button>
        </Stack>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: "18px" }}>
          <Typography sx={{ fontFamily: FONT_FAMILY }}>{error}</Typography>
        </Alert>
      )}

      {loading && (
        <Card
          elevation={0}
          sx={{
            p: 4,
            borderRadius: "26px",
            border: "1px solid #e2e8f0",
          }}
        >
          <Stack spacing={2} alignItems="center">
            <CircularProgress sx={{ color: "#4f46e5" }} />
            <Typography sx={{ fontFamily: FONT_FAMILY, color: "#64748b" }}>
              Chargement des indicateurs...
            </Typography>
            <LinearProgress
              sx={{
                width: "100%",
                maxWidth: 420,
                borderRadius: 999,
              }}
            />
          </Stack>
        </Card>
      )}

      {!loading && recommendations && (
        <>
          <QuickStats data={recommendations} navigate={navigate} />
          <PriorityActions data={recommendations} navigate={navigate} />

          {recommendations.documentAlerts?.total_alerts === 0 &&
            recommendations.maintenancePlanning?.details
              ?.vehiclesNeedingMaintenance?.length === 0 &&
            recommendations.resourceOptimization?.details
              ?.underutilizedVehicles?.length === 0 && (
              <Alert
                severity="success"
                icon={<ShieldIcon />}
                sx={{
                  mt: 3,
                  borderRadius: "18px",
                  fontFamily: FONT_FAMILY,
                }}
              >
                <Typography sx={{ fontWeight: 900 }}>
                  Excellente gestion !
                </Typography>
                <Typography>
                  Votre flotte est bien optimisée et tous les documents sont à jour.
                </Typography>
              </Alert>
            )}
        </>
      )}

      {!loading && !recommendations && !error && (
        <Alert severity="info" sx={{ borderRadius: "18px" }}>
          <Typography sx={{ fontFamily: FONT_FAMILY }}>
            Aucune donnée disponible pour le moment.
          </Typography>
        </Alert>
      )}
    </Box>
  );
}